/**
 * The outcome badge, which is the single most-repeated element in the product.
 *
 * IT SHOWS THE BUSINESS'S OWN WORD AND COLOURS BY THE GROUP. The label is whatever the
 * agent author called it - "hot", "booked", "no_show", "emergency" - and it is rendered
 * verbatim, because a boat shop's owner should read their own vocabulary rather than
 * ours. The colour comes from `group`, the coarse bucket, so a dentist's "booked" and a
 * boat shop's "hot" are the same green with no code change, and an owner renaming a
 * label does not turn a year of history grey.
 *
 * That is the whole reason `labelGroup` exists in the wire format, and it is why this
 * component maps groups rather than labels.
 */

import { humanise } from "../format";
import { cx } from "../ui";

/**
 * The groups that get a colour, and the one bit of overriding: urgent wins.
 *
 * An escalation or a safety incident is red whatever it was graded, because the point of
 * showing it in red is that somebody needs to look at it today.
 */
const TONES: Record<string, string> = {
  qualified: "bg-good-soft text-good border-good-line",
  converted: "bg-good-soft text-good border-good-line",
  booked: "bg-good-soft text-good border-good-line",
  support: "bg-warm-soft text-warm border-warm-line",
  referred: "bg-warm-soft text-warm border-warm-line",
  spam: "bg-alert-soft text-alert border-alert-line",
  urgent: "bg-alert-soft text-alert border-alert-line",
};

// An unrecognised group falls back to neutral rather than to a colour picked by a hash of
// the string: a grey badge reads as "we do not know what this means", which is true, and
// a randomly-coloured one reads as a meaning nobody assigned.
const NEUTRAL = "bg-cold-soft text-cold border-cold-line";

const BASE =
  "badge-dot inline-flex items-center gap-[5px] whitespace-nowrap rounded-full border " +
  "py-0.5 pr-[9px] pl-[8px] text-xs font-medium";

export function OutcomeBadge({
  label,
  group,
  priority,
}: {
  label: string | null;
  group: string | null;
  priority?: string | null;
}) {
  if (!label) return <span className={cx(BASE, NEUTRAL)}>ungraded</span>;

  const bucket = priority === "urgent" ? "urgent" : (group ?? "");
  return (
    <span className={cx(BASE, TONES[bucket] ?? NEUTRAL)} title={group ? `group: ${group}` : undefined}>
      {humanise(label)}
    </span>
  );
}
